import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import auth, { ACCESS_KEY } from './auth';
import distributors from './distributors';
import profile from './profile';
import file from './file';
import equipments from './equipments';
import huntertool from './huntertool';
import users from './users';
import reports from './reports';
import autocomplete from './autocomplete';
import guides from './guides';
import strategies from './strategies';
import validate from './validate';
import map from './map';
import agreements from './agreements';
import stands from './stands';
import admin from './admin';
import share from './share';
import service from './service';
import offline from './offline';
import socketService from './socket-service';
import acb from './acb';
import transaction from './transaction';
import imports from './imports';
import structure from './structure';
import aiAssistant from './ai-assistant';
import sku from './sku';
import orders from './orders';
import visitForms from './visit-forms';
import shelves from './shelves';
import tasks from './tasks';

const API_URL = process.env.REACT_APP_ENDPOINT;
const AUTH_API_URL = process.env.REACT_APP_AUTH_ENDPOINT;

export const IS_DEV = process.env.REACT_APP_ENDPOINT?.includes('.dev.');

export const generateLink = (path: string) => API_URL + path;

const instance = axios.create({
  // eslint-disable-next-line no-undef
  baseURL: API_URL,
});

const authInstance = axios.create({
  // eslint-disable-next-line no-undef
  baseURL: AUTH_API_URL,
  withCredentials: false,
  transformResponse: (data) => {
    return JSON.parse(data);
  },
  transformRequest: (data, headers) => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    headers['Content-Type'] = 'application/json';
    return JSON.stringify(data);
  },
});

const authTokenInterceptorConfig: AxiosRequestConfig = {
  withCredentials: false,
  transformResponse: (data) => {
    return JSON.parse(data);
  },
  transformRequest: (data, headers) => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
      headers['Authorization'] = token;
    }
    headers['Content-Type'] = 'application/json';
    return JSON.stringify(data);
  },
};

const authTokenInterceptorFileConfig: AxiosRequestConfig = {
  withCredentials: false,
  responseType: 'arraybuffer',
  transformResponse: (data, headers) => {
    if (headers['content-type'] === 'application/json') {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.log('not Json');
      }
    }
    
    return btoa(
      new Uint8Array(data)
        .reduce((data, byte) => data + String.fromCharCode(byte), '')
    ); // window.Buffer.from(data, 'binary').toString('base64');
  },
  transformRequest: (data, headers) => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
      headers['Authorization'] = token;
    }
    headers['Content-Type'] = 'application/json';
    return JSON.stringify(data);
  },
};

export class ResponseError<T> extends Error {
  response?: AxiosResponse<ApiResponse<T>>;
}

const api = {
  auth: auth(authInstance, {}),
  profile: profile(instance, authTokenInterceptorConfig),
  distributors: distributors(instance, authTokenInterceptorConfig),
  equipments: equipments(instance, authTokenInterceptorConfig),
  file: file(
    instance,
    authTokenInterceptorFileConfig,
    authTokenInterceptorConfig
  ),
  share: share(instance, authTokenInterceptorConfig),
  strategies: strategies(instance, authTokenInterceptorConfig),
  huntertool: huntertool(instance, authTokenInterceptorConfig),
  reports: reports(instance, authTokenInterceptorConfig),
  users: users(instance, authTokenInterceptorConfig),
  autocomplete: autocomplete(instance, authTokenInterceptorConfig),
  guides: guides(instance, authTokenInterceptorConfig),
  validate: validate(instance, authTokenInterceptorConfig),
  map: map(instance, authTokenInterceptorConfig),
  agreements: agreements(instance, authTokenInterceptorConfig),
  stands: stands(instance, authTokenInterceptorConfig),
  admin: admin(instance, authTokenInterceptorConfig),
  service: service(instance, authTokenInterceptorConfig),
  offline: offline(instance, authTokenInterceptorConfig),
  socket: socketService(instance, authTokenInterceptorConfig),
  acb: acb(instance, authTokenInterceptorConfig),
  transaction: transaction(instance, authTokenInterceptorConfig),
  imports: imports(instance, authTokenInterceptorConfig),
  structure: structure(instance, authTokenInterceptorConfig),
  aiAssistant: aiAssistant(instance, authTokenInterceptorConfig),
  sku: sku(instance, authTokenInterceptorConfig),
  orders: orders(instance, authTokenInterceptorConfig),
  visitForms: visitForms(instance, authTokenInterceptorConfig),
  shelves: shelves(instance, authTokenInterceptorConfig),
  tasks: tasks(instance, authTokenInterceptorConfig),
};

export default api;
